// Task is a collection of components that each cover a specific aspect of a distraction task (Moving target, AudioTask, etc)

import { AudioTask, AudioTaskConfiguration } from './AudioTask';
import { MovingTargetTask, MovingTargetTaskConfiguration } from './MovingTargetTask';
import { CalculationTask, CalculationTaskConfiguration } from './CalculationTask';
import { ATask, ATaskConfiguration, ITask, ITaskConfiguration } from './TaskDefinitions';
import { StroopTask, StroopTaskConfiguration } from './StroopTask';
import { ReactionTask, ReactionTaskConfiguration } from './ReactionTask';
import Phaser from 'phaser';
import { TaskTheme } from '../scene/TaskScene';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
// @ts-ignore
import deepEqual from 'deep-equal';

// let allFonts = CONSTANTS.fonts;
type Sprite = Phaser.GameObjects.Sprite;

// The complete configuration required to setup a Task.
export class CompositeTaskConfiguration extends ATaskConfiguration {
  theme?: TaskTheme;
  configurations: Array<ITaskConfiguration>;

  constructor(seed: number, configurations: ITaskConfiguration[], theme?: TaskTheme, enabled: boolean = true) {
    super(seed);

    this.theme = theme ?? new TaskTheme('default');
    this.enabled = enabled;

    this.configurations = new Array<ITaskConfiguration>().concat(configurations);
  }

  set seed(seed: number) {
    super.seed = seed;
    for (let configuration of this.configurations) {
      configuration.seed = seed;
    }
  }

  GetConfiguration<TaskConfiguration extends ITaskConfiguration>(
    target: new (...params: any[]) => TaskConfiguration
  ): TaskConfiguration {
    return this.configurations.find(t => t instanceof target) as TaskConfiguration;
  }

  ApplyConfiguration<TaskConfiguration extends ITaskConfiguration>(
    target: new (...params: any[]) => TaskConfiguration,
    apply: (t: TaskConfiguration) => void
  ) {
    apply(this.configurations.find(t => t instanceof target) as TaskConfiguration);
  }

  Copy(): CompositeTaskConfiguration {
    let config = new CompositeTaskConfiguration(
      this.seed,
      this.configurations.map(c => c.Copy()),
      this.theme,
      this.enabled
    );

    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration) {
    let compConfig = difficultyConfig as CompositeTaskConfiguration;

    // Go through all sub configurations, and update them against their matching difficulty config
    // this.GetConfiguration<ReactionTaskConfig>().ApplyDifficulty(difficultyConfig.GetConfiguration<ReactionTaskConfig>())
    // this.GetConfiguration<AudioTaskConfig>().ApplyDifficulty(difficultyConfig.GetConfiguration<AudioTaskConfig>())
    this.configurations.forEach(subConfig => {
      var subDifConfig = compConfig.configurations.find(
        subDifficultyConfig => subDifficultyConfig.constructor == subConfig.constructor
      );

      if (subDifConfig == undefined) {
        console.error('No config could be found for: ' + compConfig.constructor);
      } else {
        subConfig.ApplyDifficulty(subDifConfig);
      }
    });
  }
}

export class CompositeTask extends ATask<CompositeTaskConfiguration> {
  // The active moving target in the task (e.g.: ball)
  tasks: Array<ITask<any>>;

  constructor(configuration: CompositeTaskConfiguration, scene: Phaser.Scene) {
    super(configuration, scene, '');
    this.tasks = new Array<ITask<any>>();

    let movingTarget = new MovingTargetTask(configuration.GetConfiguration(MovingTargetTaskConfiguration), scene);

    this.tasks.push(movingTarget);
    this.tasks.push(new AudioTask(movingTarget, configuration.GetConfiguration(AudioTaskConfiguration), scene));
    this.tasks.push(new ReactionTask(movingTarget, configuration.GetConfiguration(ReactionTaskConfiguration), scene));
    this.tasks.push(new CalculationTask(configuration.GetConfiguration(CalculationTaskConfiguration), scene));
    this.tasks.push(new StroopTask(configuration.GetConfiguration(StroopTaskConfiguration), scene));
  }

  GetTask<Task extends ITask<any>>(target: new (...params: any[]) => Task): Task {
    return this.tasks.find(t => t instanceof target) as Task;
  }

  Create(configuration: CompositeTaskConfiguration, scene: Phaser.Scene): void {
    this.tasks.forEach(t => {
      configuration.configurations.forEach(config => {
        if (t.Accepts(config)) {
          t.Create(config, scene);
        }
      });
    });
  }

  // Starts or resumes the task and ensures it's visible
  Start(scene: Phaser.Scene) {
    this.isActive = true;

    this.tasks.forEach(t => {
      if (t.configuration.enabled) t.Start(scene);
    });
  }

  // Pauses / hides the task
  Stop(scene: Phaser.Scene) {
    this.isActive = false;

    this.tasks.forEach(t => {
      if (t.isActive) t.Stop(scene);
    });
  }

  OnUpdate(
    oldConfiguration: CompositeTaskConfiguration,
    configuration: CompositeTaskConfiguration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ) {
    this.tasks.forEach(t => {
      // If a task configuration has changed, attempt to update the task.
      // If the task needs to start, or stop, do so before & after the update.
      let oldTaskConfiguration = oldConfiguration.configurations.find(config => t.Accepts(config));
      let newTaskConfiguration = configuration.configurations.find(config => t.Accepts(config));

      if (oldTaskConfiguration == undefined || newTaskConfiguration == undefined) {
        throw new Error(`No configuration found for task: ${t}`);
      }
      let updateBoolean = deepEqual(newTaskConfiguration, oldTaskConfiguration, { strict: true });

      if (updateBoolean) return;

      // console.log(
      //   `[CompositeTask::OnUpdate] Changed Task Configuration: ${t.name}`,
      //   oldTaskConfiguration,
      //   newTaskConfiguration
      // );

      let needsToStart = this.isActive && !t.isActive && newTaskConfiguration.enabled;
      if (needsToStart) {
        t.Start(scene);
      }
      // console.log('Updated composite task', t.name)
      t.Update(newTaskConfiguration, scene, firstCreateCall);

      // We need to call stop on a task if: the task is active AND new config is NOT enabled.
      let needsToStop = t.isActive && !newTaskConfiguration.enabled;
      if (needsToStop) {
        // console.log('did we stop the timeline', t.name)
        t.Stop(scene);
      }
    });
  }

  Render() {
    this.tasks.forEach(t => t.Render());
  }

  HandleUserResponseFromServer(isCorrect: boolean) {
    super.HandleUserResponseFromServer(isCorrect);

    this.tasks.forEach(t => {
      t.HandleUserResponseFromServer(isCorrect);
    });
  }
}
